'use client'

import { html } from '@ensemble.music/ui'
import { AnimatePresence, MotionConfig, motion } from 'motion/react'
import { type ReactNode, useEffect, useRef } from 'react'
import { BRAND_NAME } from '../landingpage/content'

const easeOut = [0.22, 1, 0.36, 1] as const

/** The ensemble wordmark as a link; every header uses it. */
export function LogoLink({ href, label = 'zur Startseite' }: { href: string; label?: string }) {
  return (
    <html.a
      href={href}
      className="flex flex-row items-center shrink-0 no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-ring"
      aria-label={`${BRAND_NAME}, ${label}`}
    >
      <img
        src="/images/logo.svg"
        alt=""
        width={124}
        height={24}
      />
    </html.a>
  )
}

/** The logo-only bar the step forms and /fuer-bands share; `action` is an optional right slot. */
export function LogoBar({ href, action }: { href: string; action?: ReactNode }) {
  return (
    <html.div className="flex flex-row items-center justify-between gap-4 w-full h-16 max-w-[1120px] mx-auto px-8 max-sm:px-4">
      <LogoLink href={href} />
      {action}
    </html.div>
  )
}

/**
 * The sticky header every page without its own navigation gets (status, thank-you, legal, admin),
 * so the logo is always on top. `tone="admin"` tints it blue to tell the admin area apart.
 */
export function SiteHeader({
  href = '/buchen',
  action,
  tone,
}: {
  href?: string
  action?: ReactNode
  tone?: 'admin'
}) {
  return (
    <header className="landing-nav">
      <html.div
        className={`w-full h-full border-b ${
          tone === 'admin'
            ? 'bg-primary-soft border-primary shadow-sm'
            : 'bg-background border-border-color-subtle'
        }`}
      >
        <LogoBar
          href={href}
          action={action}
        />
      </html.div>
    </header>
  )
}

/**
 * A form in full-screen steps (owner request 2026-10-18): the logo bar stays, a blue strip below it
 * grows from the left with every step, and the step itself slides in from the side it came from.
 * Used by the band signup and the booking request.
 *
 * `direction` is +1 for forward and -1 for back, so "Zurück" slides the other way.
 */
export function WizardShell({
  homeHref,
  steps,
  current,
  direction,
  children,
}: {
  homeHref: string
  steps: readonly string[]
  current: number
  direction: 1 | -1
  children: ReactNode
}) {
  const headingRef = useRef<HTMLDivElement>(null)
  const first = useRef(true)

  // Focus follows the step, so a screen reader announces the new heading and Tab starts there.
  // Not on the first render: landing on the page must not steal focus from the browser.
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    window.scrollTo({ top: 0 })
    headingRef.current?.querySelector<HTMLElement>('[data-step-heading]')?.focus()
  }, [current])

  return (
    // reducedMotion="user": under prefers-reduced-motion the slides drop to a plain fade
    <MotionConfig reducedMotion="user">
      <html.div className="flex flex-col min-h-screen bg-background">
        <header className="wizard-top">
          <html.div className="w-full bg-background border-b border-border-color-subtle">
            <LogoBar href={homeHref} />
          </html.div>
          <div
            className="wizard-progress"
            role="progressbar"
            aria-label="Fortschritt"
            aria-valuemin={1}
            aria-valuemax={steps.length}
            aria-valuenow={current + 1}
            aria-valuetext={`Schritt ${current + 1} von ${steps.length}: ${steps[current]}`}
          >
            <motion.div
              className="wizard-progress-fill"
              initial={false}
              animate={{ scaleX: (current + 1) / steps.length }}
              transition={{ duration: 0.6, ease: easeOut }}
            />
          </div>
        </header>

        <main
          id="inhalt"
          className="wizard-stage"
        >
          <AnimatePresence
            mode="wait"
            initial={false}
            custom={direction}
          >
            <motion.div
              key={current}
              ref={headingRef}
              className="wizard-step"
              custom={direction}
              variants={{
                enter: (dir: number) => ({ opacity: 0, x: dir * 72 }),
                center: { opacity: 1, x: 0 },
                exit: (dir: number) => ({ opacity: 0, x: dir * -72 }),
              }}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.32, ease: easeOut }}
            >
              <html.div className="flex flex-col items-center gap-2 text-center">
                <html.p className="text-sm font-semibold text-primary-text">
                  Schritt {current + 1} von {steps.length}
                </html.p>
                <h1
                  data-step-heading
                  tabIndex={-1}
                  className="wizard-step-heading"
                >
                  {current + 1}. {steps[current]}
                </h1>
              </html.div>
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </html.div>
    </MotionConfig>
  )
}

/** One block of a step that rises in after the heading - fields, then the buttons. */
export function StepBlock({ index, children }: { index: number; children: ReactNode }) {
  return (
    <motion.div
      className="wizard-block"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.36, delay: 0.12 + 0.07 * index, ease: easeOut }}
    >
      {children}
    </motion.div>
  )
}
